import { AxiosResponse } from 'axios'
import { http } from '../../utils'
import {
  Response,
  Customer,
  EntryType,
  TimeTypeEnum,
  Item,
  ItemCategory,
} from '../types'
import { EwayAddress, EwayItem, EwayParty } from 'src/content/applications/slips/eway/Services'

export type AddStockItemRequest = {
  id?: number
  area: number
  availableQuantity: number
  isActive: boolean
  itemName: string
  length: number
  width: number
  rate: number
  size: string
  category: any
  issuedQuantity: number
  isPlate: boolean
  totalQuantity: number
  runningLength: number
  hsnCode?: string
  weight?: number
  value?: number
}

export type IssueItem = Pick<Item, 'itemName' | 'rate' | 'id'>
// type WithPartial<T, K extends keyof T> = T & { [P in K]?: T[P] }

/**
 * Billing payload for cycle-based rent on issue/challan entries (POST /bill/pvt/v2).
 * Do not send `extraDayChargeSlabs` on create — backend derives extra-day rules elsewhere.
 */
export type cycleRatePlan = {
  cycleDays: number
  cycleCharge: number
  /** cycleCharge / cycleDays when that division is finite; omitted otherwise. */
  perDayRate?: number
  extraDayChargeSlabs?: { uptoDays: number; charge: number }[]
}

/** GET /entry/pvt — nested plan on an entry. */
export type EntryCycleRatePlanApi = {
  id?: number
  cycleDays: number
  cycleCharge: number
  /**
   * Backend may send slabs either as parsed array or legacy JSON string.
   */
  extraDayChargeSlabs?:
    | Array<{ id?: number; uptoDays: number; charge: number }>
    | string
  /** Mutually exclusive with perDayFixed and extraDayChargeSlabs in PUT. */
  perDayRate?: number
  /** Mutually exclusive with perDayRate and extraDayChargeSlabs in PUT. */
  perDayFixed?: number
  createDate?: string
  updateDate?: string
}

/** PUT /entry/pvt — update payload (omit slab ids unless backend requires). */
export type EditItemCycleRatePlanRequest = {
  id?: number
  cycleDays: number
  cycleCharge: number
  extraDayChargeSlabs?: { uptoDays: number; charge: number }[]
  perDayRate?: number
  perDayFixed?: number
}

export type PrepareIssueItem = {
  item: {
    id: number
    label: string
    category?: string
  }
  entryType: EntryType
  quantity?: number
  rate?: number
  /** Custom charge title for OtherCharges lines on challan (POST /bill/pvt/v2) */
  chargeTitle?: string
  timeType?: string
  lostItemCost?: number
  minRentalDays?: number
  height?: number
  width?: number
  length?: number
  outAreaEntryId?: number
  areaOutEntryId?: number
  isArea?: boolean
  isAreaV2?: boolean
  siteTransferRate?: number
  excessQuantity?: number
  secondaryType?: 'EXCESS_QUANTITY'
  /** When set (monthly/custom cycle), API uses rate 0 and this plan instead of daily rate. */
  cycleRatePlan?: cycleRatePlan
}

export type IssueItemRequest = {
  items: PrepareIssueItem[]
  quotationItems?: PrepareIssueItem[]
  customerId: number
  date_entry: string
  site_address?: string
  bill_type?: string
  time?: string
  deposite?: number
  ceiling_area?: string
  rate_per_sqft?: string
  bill_id?: number
  billStatus?: string
  vehicleNo?: string
  challanNo?: string
  challanId?: string
  driverName?: string
  fromSiteId?: number
  fromCustomerId?: number
}

export type CategoryItemsResponse = {
  items: Item[]
  category: ItemCategory
}

export interface FetchItemResponse extends Response {
  data: CategoryItemsResponse
}

export function fetchItemList(categoryId?: number) {
  let url = '/item/pvt/v2'
  if (categoryId) {
    url = `/item/pvt/v2?category_id=${categoryId}`
  }
  return http.get(url).then((response: AxiosResponse) => response)
}

/**
 * This API call is to populate the select item dropdown while issuing the item
 * @returns
 */
export function fetchSelectItemOptions(
  customer_id: number,
  agreementId?: number,
  siteId?: number
) {
  let url = `/item/pvt/v2/stock/items?customer_id=${customer_id}`
  if (agreementId) {
    url = `/item/pvt/v2/stock/items?customer_id=${customer_id}&agreement_id=${agreementId}`
  }
  if (siteId) {
    url = `${url}&from_site=${siteId}`
  }
  return http.get(url).then((response: AxiosResponse) => response)
}

/** Item shape from GET /item/pvt/v3 (id + itemName only) */
export type ItemV3 = { id: number; itemName: string }

export interface GetItemsV3Response extends Response {
  data: ItemV3[]
}

export function getItemsV3() {
  return http.get<GetItemsV3Response>('/item/pvt/v3')
}

export interface Bill {
  id: number
  discount: number
  daysDiscount: number
  total: number
  subtotal: number
  lostItemsCost: number
  otherCharges: number
  status: string
  dailyBillAmount: number
  totalPaid: number
  totalRent: number
  totalRentWithoutConsumable: number
  consumableTotal: number
  wallsAreaRent: number
  sellItemsTotalCost: number
  totalCeilingArea: number
  totalPlateArea: number
  wallAreaPercentage: number
  totalDue: number
  outstandingTillLastEntry: number
  customer: Customer
  openDate: string
  createDate: string
  closeDate: string
  ratePerSqft: number
  billAgeInDays: number
  siteAddress: string
  siteAddress2?: string
  billType: string
  isSqftFlatRate: boolean
  sqftStartDate: string
  sqftEndDate: string
  updateDate: string
  isGSTEnabled: boolean
  isRecurringEnabled: boolean
  agreementTitle?: string
  agreementId?: number
  state?: string
  stateCode?: string
  gstType?: string
  sitePlace?: string
  sitePincode?: string
  supplyNature?: string
  is30DaysMonthEnabled?: boolean
  remainingPieces?: number
  overDaysRate?: number
  sqftMinDays?: number
  cgstRate?: number
  sgstRate?: number
  igstRate?: number
  openingBalance?: number
  accountType?: string
  contactDetails?: string
  siteEmail?: string
  additionalInfo?: string
  docNo?: string
  slabRates?: {
    id?: number
    firstSlabDays?: number
    firstSlabRate?: number
    secondSlabDays?: number
    secondSlabRate?: number
    thirdSlabDays?: number
    thirdSlabRate?: number
    overDaysPerDayRate?: number
    isSqftBased: boolean
  }
}

export type AccountRequest = {
  date?: string
  customerId: number
  billId?: number
  siteAddress?: string
  siteAddress2?: string
  billType: string
  ceilingArea: number
  wallPercent: number
  isSqftFlatRate: boolean
  isGSTEnabled: boolean
  enteries: any
  customerName?: string
  customerMobile?: string
  customerAddress?: string
  billStatus?: string
  customerGSTIN?: string
  discount?: number
  isRecurringEnabled: boolean
  agreementTitle?: string
  agreementId?: number
  deposit?: number
  quotationEntries?: any
  state?: string
  stateCode?: string
  gstType?: string
  sitePlace?: string
  sitePincode?: number
  supplyNature?: string
  is30DaysMonthEnabled?: boolean
  openingBalance?: number
  accountType?: string
  contactDetails?: string
  siteEmail?: string
  additionalInfo?: string
  slabRates?: {
    id?: number
    firstSlabDays?: number
    firstSlabRate?: number
    secondSlabDays?: number
    secondSlabRate?: number
    thirdSlabDays?: number
    thirdSlabRate?: number
    overDaysPerDayRate?: number
    isSqftBased: boolean
  }
}

export interface IssueItemResponse extends Response {
  data?: Bill
}

function computeIssueCycleRatePerDay(
  cycleCharge: number,
  cycleDays: number
): number | undefined {
  if (
    typeof cycleCharge !== 'number' ||
    typeof cycleDays !== 'number' ||
    !Number.isFinite(cycleCharge) ||
    !Number.isFinite(cycleDays) ||
    cycleDays <= 0
  ) {
    return undefined
  }
  const q = cycleCharge / cycleDays
  if (!Number.isFinite(q)) {
    return undefined
  }
  // 4 decimal places (e.g. 20/30 → 0.6667)
  return Math.round(q * 10000) / 10000
}

/** Shapes each challan line for POST /bill/pvt/v2 (daily vs cycleRatePlan). */
export function normalizeIssueEntryForApi(entry: PrepareIssueItem): PrepareIssueItem & {
  rate?: number
  cycleRatePlan?: cycleRatePlan
} {
  const { cycleRatePlan, ...rest } = entry
  const result = { ...rest } as PrepareIssueItem & {
    rate?: number
    cycleRatePlan?: cycleRatePlan
  }

  const plan = cycleRatePlan
  const planOk =
    plan &&
    typeof plan.cycleDays === 'number' &&
    plan.cycleDays >= 1 &&
    typeof plan.cycleCharge === 'number' &&
    plan.cycleCharge >= 0

  if (planOk) {
    result.rate = 0
    const perDayRate = computeIssueCycleRatePerDay(plan.cycleCharge, plan.cycleDays)
    result.cycleRatePlan = {
      cycleDays: plan.cycleDays,
      cycleCharge: plan.cycleCharge,
      ...(perDayRate !== undefined ? { perDayRate } : {}),
    }
  } else {
    delete result.cycleRatePlan
  }

  return result
}

// Issue Item Request
export function issueItem(request: IssueItemRequest) {
  const totalPaid = request.deposite ? request.deposite : 0

  const accountRequest = {
    enteries: request.items.map(normalizeIssueEntryForApi),
    quotationEntries: request.quotationItems?.map(normalizeIssueEntryForApi),
    customerId: request.customerId,
    date: request.date_entry,
    siteAddress: request.site_address,
    billType: request.bill_type,
    deposit: totalPaid,
    ceilingArea: request.ceiling_area,
    ratePerSqft: request.rate_per_sqft,
    billId: request.bill_id,
    billStatus: request.billStatus,
    vehicleNo: request.vehicleNo,
    challanNo: request.challanNo,
    challanId: request.challanId,
    driverName: request.driverName,
    fromSiteId: request.fromSiteId,
    fromCustomerId: request.fromCustomerId
  }
  return http
    .post('/bill/pvt/v2', accountRequest)
    .then((response: AxiosResponse) => response)
}

// Update Issued Item
export type EditItemRequest = {
  id: number
  entryDate?: string
  quantity?: number
  rate?: number
  weight?: number
  timeType?: TimeTypeEnum
  receivedAmount?: number
  lostItemCost?: number
  isQuotation?: boolean
  challanNo?: string
  challanId?: string
  hsnCode?: string
  height?: number | string
  width?: number | string
  length?: number | string
  minRentalDays?: number
  cycleRatePlan?: EditItemCycleRatePlanRequest
}

export type EntityType = 'ItemEntry' | 'Customer' | 'Payment' | string

export type UpdateEntryFieldRequest = {
  entryId: number
  fieldName: 'rate' | 'quantity' | string
  entity: EntityType
  value: number | string
  /** Required for `site_item_rate` / `site_category_rate` entities (View Slips Rates tab). */
  siteId?: number
}

export interface UpdateEntryFieldResponse extends Response {
  data?: {
    id: number
    [key: string]: any
  }
}

export type SlipPDFRequest = {
  billId: number
  showRate: boolean
  slipType: string
  title?: string
  isQuotation?: boolean
  isRojnamcha?: boolean
  from?: string
  to?: string
  showGoodsValue?: boolean
  showOnlyGoodValue?: boolean
  hideTotalEntries?: boolean
  challanId?: number
}

export type SiteChallansPDFRequest = {
  billId: number
}

export type EwayBillRequest = {
  accountId: number
  challanId: number
  challanNumber: string
  entryType: string
  transactionType: number
  distance: number
  vehicleNo: string
  billFrom: EwayParty
  billTo: EwayParty
  dispatchFrom: EwayAddress
  shipTo: EwayAddress
  transporterId?: string
  transporterName?: string
  transDocNo?: string
  transDocDate?: string | null
  itemList: EwayItem[]
  totalValue: number
  totInvoiceValue: number
  cgstValue: number
  sgstValue: number
  igstValue: number
  docDate: string
}

export type CancelEwayBillRequest = {
  ewbNo: number
  cancelRsnCode: number
  cancelRmrk?: string
  challanId: number
}

export type UpdatePartEWayBillRequest = {
  ewbNo: number
  vehicleNo: string
  fromPlace: string
  fromState: number
  reasonCode: string
  reasonRem?: string
  transDocNo?: string
  transDocDate?: string
  transMode: string
  vehicleType: string
}

export type AddPaymentRequest = {
  billId: number
  paymentId?: number
  paymentDate?: string
  amount?: number
  note?: string
  refNo?: string
  tdsAmount?: number
  tdsRate?: number
  tdsSection?: string
}

export type AddPaymentResponse = {
  id: number
  amount: number
  note?: string
  paymentDate: string
}

export interface AddPaymentAPIResponse extends Response {
  data: AddPaymentResponse
}

export interface DeletePaymentAPIResponse extends Response {
  data: boolean
}

export interface SlipPDFAPIResponse extends Response {
  data: string
}

export interface AddStockItemAPIResponse extends Response {
  data: null
}

export interface DeleteItemAPIResponse extends Response {
  data: Item | null
}
export interface DeleteChallanAPIResponse extends Response {
  data: boolean | null
}

export type UpdateIssuedItemResponse = {
  id: number
  paymentId: number
  minRentalDays: number
  receivedAmount: number
  quantity: number
  lostItemCost: number
  entryDate: string
  timeType: TimeTypeEnum
  rate: number
}
export interface UpdateIssuedItemAPIResponse extends Response {
  data: UpdateIssuedItemResponse
}

export interface PendingItemEntry {
  itemName: string
  quantity: number
  issueDate: string
}

export interface PendingItemsData {
  name
  customerMobile: string
  billEntries: [PendingItemEntry]
}
export interface FetchPendingItemsAPIResponse extends Response {
  data: PendingItemsData[]
}

export type EWayBillData = {
  challanId: number
  ewayBillLink: string
  ewayBillNo: string
}

export interface CreateEWayBillAPIResponse extends Response {
  data: EWayBillData
}

export type GSTINDetailsResponse = {
  gstin: string
  tradeName: string
  legalName: string
  address1: string
  address2: string
  stateCode: string
  pinCode: string
  txpType: string
  status: string
  blkStatus: string
}

export interface GSTINDetailsAPIResponse extends Response {
  data: GSTINDetailsResponse
}

// Issue Item Request
export function updateIssuedItem(request: EditItemRequest) {
  return http
    .put('/entry/pvt/', request)
    .then((response: AxiosResponse) => response)
}

export function updateEntryField(request: UpdateEntryFieldRequest) {
  const body = {
    entryId: request.entryId,
    fieldName: request.fieldName,
    value: request.value,
  }
  if (request.entity === 'site_item_rate' && request.siteId != null) {
    return http
      .put<Response>(
        `/item/pvt/item_rate/inline_update?site_id=${request.siteId}`,
        body
      )
      .then((response: AxiosResponse<Response>) => response)
  }
  if (request.entity === 'site_category_rate' && request.siteId != null) {
    return http
      .put<Response>(
        `/item/pvt/category_rate/inline_update?site_id=${request.siteId}`,
        body
      )
      .then((response: AxiosResponse<Response>) => response)
  }
  if (request.entity === 'agreement_title') {
    return http
      .put<Response>('/bill/pvt/agreement_title_inline', body)
      .then((response: AxiosResponse<Response>) => response)
  }
  // Convert entity to endpoint: entry -> /entry/pvt/inline_update
  const entityEndpoint = `/${request.entity}/pvt/inline_update`
  return http
    .put(entityEndpoint, body)
    .then((response: AxiosResponse) => response)
}

/** GET /item/pvt/item_rates/{siteId} — category groups with per-item rates for a site/bill. */
export type SiteRateItemRow = {
  itemId: number
  itemName: string
  rate: number | null
  cycleRates?: string | null
  warning?: string | null
  cycleRatePlan?: EntryCycleRatePlanApi | null
}

/** PUT /item/pvt/update_site_rate — plain rate and/or cycle plan (cycle wins if both sent). */
export type UpdateSiteItemRateRequest = {
  siteId: number
  /** Set for item-level update; omit when updating category. */
  itemId?: number
  /** Set for category-level update; omit when updating item. */
  categoryId?: number
  rate?: number
  cycleRatePlan?: EditItemCycleRatePlanRequest | null
}

export type SiteRateCategoryGroup = {
  name: string
  categoryId: number
  rate: number | null
  isInlineEditable?: boolean
  cycleRatePlan?: EntryCycleRatePlanApi | null
  items: SiteRateItemRow[]
}

export interface FetchSiteItemRatesResponse extends Response {
  data?: SiteRateCategoryGroup[]
}

export type SiteRateInlineUpdateRequest = {
  entryId: number
  fieldName: 'rate'
  value: number
}

export function fetchSiteItemRates(siteId: number) {
  return http
    .get<FetchSiteItemRatesResponse>(`/item/pvt/item_rates/${siteId}`)
    .then((response: AxiosResponse<FetchSiteItemRatesResponse>) => response.data)
}

export function updateSiteItemRate(
  siteId: number,
  body: SiteRateInlineUpdateRequest
) {
  return updateEntryField({
    entryId: body.entryId,
    fieldName: body.fieldName,
    entity: 'site_item_rate',
    value: body.value,
    siteId,
  }).then((response: AxiosResponse<Response>) => response.data)
}

export function updateSiteCategoryRate(
  siteId: number,
  body: SiteRateInlineUpdateRequest
) {
  return updateEntryField({
    entryId: body.entryId,
    fieldName: body.fieldName,
    entity: 'site_category_rate',
    value: body.value,
    siteId,
  }).then((response: AxiosResponse<Response>) => response.data)
}

export function updateSiteItemRateFull(body: UpdateSiteItemRateRequest) {
  const hasItem = body.itemId != null
  const hasCategory = body.categoryId != null
  if (hasItem === hasCategory) {
    return Promise.reject(
      new Error(
        'update_site_rate requires exactly one of itemId or categoryId'
      )
    )
  }
  return http
    .put<Response>('/item/pvt/update_site_rate', body)
    .then((response: AxiosResponse<Response>) => response.data)
}

export function reapplyItemArea(siteId: number) {
  return http
    .put(`/entry/pvt/reapply_area?site_id=${siteId}`)
    .then((response: AxiosResponse) => response)
}

export function resetWeightValue(siteId: number) {
  return http
    .put(`/entry/pvt/reset_weight_value?site_id=${siteId}`)
    .then((response: AxiosResponse) => response)
}

export function reapplyHsnSacCode(siteId: number) {
  return http
    .put(`/entry/pvt/reapply_hsn_sac_code?site_id=${siteId}`)
    .then((response: AxiosResponse) => response)
}

export function deleteChallan(id: number) {
  return http
    .delete(`/challan/pvt?challan_id=${id}`)
    .then((response: AxiosResponse) => response)
}

// Add payment Request
export function addPayment(request: AddPaymentRequest) {
  return http
    .post('/payment/pvt', request)
    .then((response: AxiosResponse) => response)
}

/** Full payment row for edit dialog (GET payment/pvt?payment_id=) */
export function getPaymentById(paymentId: number) {
  return http
    .get('/payment/pvt', { params: { payment_id: paymentId } })
    .then((response: AxiosResponse) => response)
}

// Delete payment Request
export function deletePayment(id: number) {
  return http
    .delete(`payment/pvt/delete?payment_id=${id}`)
    .then((response: AxiosResponse) => response)
}

// Slip pdf
export function getSlipPDF(request: SlipPDFRequest) {
  return http
    .post('/bill/pvt/slip_pdf', request)
    .then((response: AxiosResponse) => response)
}

// Site challans summary pdf
export function getSiteChallansPDF(request: SiteChallansPDFRequest) {
  return http
    .post('/bill/pvt/site_challans_pdf', request)
    .then((response: AxiosResponse) => response)
}

// Site challans summary excel
export function getSiteChallansExcel(request: SiteChallansPDFRequest) {
  return http
    .post('/bill/pvt/site_challans_excel', request)
    .then((response: AxiosResponse) => response)
}

export function getEwayBill(eWayBillNumber: number) {
  return http
    .get(`/eway_bill/pvt?eway_bill_no=${eWayBillNumber}`)
    .then((response: AxiosResponse) => response)
}

export function createEwayBill(request: EwayBillRequest) {
  return http
    .post(`${process.env.EWAY_API_BASE_URL}ewaybill/v2`, request)
    .then((response: AxiosResponse) => response)
}

export function cancelEwayBill(request: CancelEwayBillRequest) {
  return http
    .post(`${process.env.EWAY_API_BASE_URL}ewaybill/cancel`, request)
    .then((response: AxiosResponse) => response)
}

export function updatePartBEwayBill(request: UpdatePartEWayBillRequest) {
  return http
    .post(`${process.env.EWAY_API_BASE_URL}ewaybill/update_part_b`, request)
    .then((response: AxiosResponse) => response)
}

export function viewEwayBill(eWayBillNumber: number) {
  return http
    .get(
      `${process.env.EWAY_API_BASE_URL}ewaybill/view_pdf?eWayNo=${eWayBillNumber}`
    )
    .then((response: AxiosResponse) => response)
}

export function getGSTNDetails(gstin: string) {
  return http
    .get(`${process.env.EWAY_API_BASE_URL}gst/gstin?gstin=${gstin}`)
    .then((response: AxiosResponse) => response)
}

export function getEntriesByDate(startDate: string, endDate: string) {
  return http
    .get(`/entry/pvt/entries_bw_dates?from=${startDate}&to=${endDate}`)
    .then((response: AxiosResponse) => response)
}

// Slip pdf
export function addStockItem(request: AddStockItemRequest) {
  return http
    .post('/item/pvt', request)
    .then((response: AxiosResponse) => response)
}

// Slip pdf
export function deleteStockItem(id: number) {
  return http
    .delete(`/item/pvt/${id}`)
    .then((response: AxiosResponse) => response)
}

// Pending items
export function getPendingItems(itemId: string) {
  return http
    .get(`/entry/pvt/pending_items/${itemId}`)
    .then((response: AxiosResponse) => response)
}

export type AddItemStockType = 'PURCHASE' | 'OPENING_STOCK' | 'ADJUSTMENT' | 'SCRAP'

export interface AddItemStockRequest {
  itemId: number
  type: AddItemStockType
  quantity: number
  vendorId?: number
  rate?: number
  transactionDate?: string
  reference?: string
}

export interface AddItemStockResponse {
  success: boolean
  message: string
  data?: {
    id: number
    type: string
    quantity: number
    transactionDate: string
    reference?: string
    createDate: string
  }
}

export function addItemStock(request: AddItemStockRequest) {
  return http
    .post<AddItemStockResponse>('/item/pvt/stock/add', request)
    .then((response: AxiosResponse<AddItemStockResponse>) => response.data)
}

export interface AddItemStockBatchItem {
  itemId: number
  quantity: number
  rate?: number
}

export interface AddItemStockBatchRequest {
  type: AddItemStockType
  transactionDate?: string
  reference?: string
  vendorId?: number
  invoiceNo?: string
  invoiceValue?: number
  items: AddItemStockBatchItem[]
}

export interface AddItemStockBatchResponse {
  success: boolean
  message: string
  data?: {
    id: number
    type: string
    quantity: number
    transactionDate: string
    reference?: string
    createDate: string
  }
}

export function addItemStockBatch(request: AddItemStockBatchRequest) {
  return http
    .post<AddItemStockBatchResponse>('/item/pvt/stock/add/batch', request)
    .then((response: AxiosResponse<AddItemStockBatchResponse>) => response.data)
}

// Stock invoice batch creation
export interface StockInvoiceItem {
  itemId: number
  quantity: number
}

export type StockInvoiceTransactionType =
  | 'PURCHASE'
  | 'SCRAP'
  | 'ADJUSTMENT'
  | 'OPENING_STOCK'
  | 'SOLD'

export interface StockInvoiceRequest {
  invoiceNo?: string
  transactionDate: string
  reference?: string
  invoiceValue?: number
  transactionType: StockInvoiceTransactionType
  vendorId: number
  items: StockInvoiceItem[]
}

export interface StockInvoiceResponse extends Response {
  data?: {
    id: number
  } | null
}

export function createStockInvoice(request: StockInvoiceRequest) {
  return http
    .post<StockInvoiceResponse>('/stock_invoice/pvt', request)
    .then((response: AxiosResponse<StockInvoiceResponse>) => response.data)
}

export interface AddStockInvoiceItemsLine {
  itemId: number
  quantity: number
  rate: number
}

export interface AddStockInvoiceItemsRequest {
  invoiceId: number
  items: AddStockInvoiceItemsLine[]
}

export interface AddStockInvoiceItemsResponse extends Response {
  data?: unknown
}

export function addItemsToStockInvoice(body: AddStockInvoiceItemsRequest) {
  return http
    .post<AddStockInvoiceItemsResponse>('/stock_invoice/pvt/add_items', body)
    .then((response: AxiosResponse<AddStockInvoiceItemsResponse>) => response.data)
}

export interface NextStockInvoiceNoResponse extends Response {
  data?: string | null
}

export function getNextStockInvoiceNo(): Promise<NextStockInvoiceNoResponse> {
  return http
    .get<NextStockInvoiceNoResponse>('/stock_invoice/pvt/next_invoice_no')
    .then((response: AxiosResponse<NextStockInvoiceNoResponse>) => response.data)
}

// Stock invoice list (grouped) – GET /stock_invoice/pvt
export interface StockInvoiceListVendor {
  id: number
  name: string
}

export interface StockInvoiceListTransaction {
  id: number
  item: { id: number; itemName: string }
  type: string
  quantity: number
  transactionDate: string
  reference: string | null
  rate?: number | null
  invoiceNo?: string | null
  invoiceValue?: number | null
  vendor?: StockInvoiceListVendor | null
  createDate: string
}

export interface StockInvoiceListGroup {
  id: number
  invoiceNo: string | null
  invoiceDate: string
  createDate: string
  updateDate: string
  invoiceValue: number | null
  reference: string | null
  transactionType: string
  vendor: StockInvoiceListVendor | null
  transactions: StockInvoiceListTransaction[]
}

export interface StockInvoiceListData {
  totalPurchaseInvoiceValue: number
  totalPurchaseQuantity: number
  totalScrapInvoiceValue: number
  totalScrapQuantity: number
  totalSoldInvoiceValue?: number
  totalSoldQuantity?: number
  invoices: StockInvoiceListGroup[]
}

export interface GetStockInvoiceListParams {
  from?: string
  to?: string
  transaction_type?: string
  vendor_id?: number
}

export interface GetStockInvoiceListResponse {
  success: boolean
  message: string | null
  data: StockInvoiceListData
}

export function getStockInvoices(params?: GetStockInvoiceListParams) {
  const searchParams = new URLSearchParams()
  if (params?.from) searchParams.set('from', params.from)
  if (params?.to) searchParams.set('to', params.to)
  if (params?.transaction_type) searchParams.set('transaction_type', params.transaction_type)
  if (params?.vendor_id != null) searchParams.set('vendor_id', String(params.vendor_id))
  const query = searchParams.toString()
  const url = query ? `/stock_invoice/pvt?${query}` : '/stock_invoice/pvt'
  return http
    .get<GetStockInvoiceListResponse>(url)
    .then((response: AxiosResponse<GetStockInvoiceListResponse>) => response.data)
}

export interface UpdateStockInvoiceRequest {
  invoiceNo?: string
  transactionDate: string
  reference?: string
  invoiceValue?: number
  transactionType: StockInvoiceTransactionType
  vendorId: number
}

export interface UpdateStockInvoiceResponse {
  success: boolean
  message?: string | null
}

export interface DeleteStockInvoiceResponse {
  success: boolean
  message?: string | null
}

export function updateStockInvoice(id: number, body: UpdateStockInvoiceRequest) {
  return http
    .put<UpdateStockInvoiceResponse>(`/stock_invoice/pvt/${id}`, body)
    .then((response: AxiosResponse<UpdateStockInvoiceResponse>) => response.data)
}

export function deleteStockInvoice(id: number) {
  return http
    .delete<DeleteStockInvoiceResponse>(`/stock_invoice/pvt/${id}`)
    .then(
      (response: AxiosResponse<DeleteStockInvoiceResponse>) =>
        response.data ?? { success: true }
    )
}

// Stock transaction list and CRUD
export interface StockTransactionCustomer {
  id: number
  name: string
}

export interface StockTransactionListItem {
  id: number
  type: string
  quantity: number
  rate?: number | null
  transactionDate: string
  reference: string | null
  createDate: string
  invoiceNo?: string | null
  invoiceValue?: number | null
   /** Optional: linked item for this transaction */
  item?: {
    id: number
    itemName: string
  } | null
  /** Optional: linked vendor/customer for this transaction */
  customer?: StockTransactionCustomer | null
  vendor?: StockTransactionCustomer | null
}

export interface StockTransactionsData {
  transactions: StockTransactionListItem[]
  purchaseQuantity: number
  purchaseCost: number
  scrappedQuantity: number
  scrappedCost: number
}

export interface GetStockTransactionsResponse {
  success: boolean
  message: string
  data: StockTransactionsData
}

export interface GetStockTransactionByIdResponse {
  success: boolean
  message: string
  data: StockTransactionListItem
}

export function getStockTransactionById(id: number) {
  return http
    .get<GetStockTransactionByIdResponse>(`/item/pvt/stock-transaction/${id}`)
    .then((response: AxiosResponse<GetStockTransactionByIdResponse>) => response.data)
}

export interface GetStockTransactionsParams {
  from?: string
  to?: string
  transactionType?: 'PURCHASE' | 'SCRAP' | 'ADJUSTMENT' | 'OPENING_STOCK'
  vendorId?: number
}

export function getStockTransactions(params?: GetStockTransactionsParams) {
  const searchParams = new URLSearchParams()
  if (params?.from) searchParams.set('from', params.from)
  if (params?.to) searchParams.set('to', params.to)
  if (params?.transactionType) searchParams.set('transaction_type', params.transactionType)
  if (params?.vendorId != null) searchParams.set('vendor_id', String(params.vendorId))
  const query = searchParams.toString()
  const url = query ? `/item/pvt/stock-transaction?${query}` : '/item/pvt/stock-transaction'
  return http
    .get<GetStockTransactionsResponse>(url)
    .then((response: AxiosResponse<GetStockTransactionsResponse>) => response.data)
}

export interface StockTransactionExportResponse {
  success: boolean
  message: string | null
  data: string | null
  code: number
}

export interface StockTransactionExportParams {
  vendorId?: number | null
  from?: string
  to?: string
  /** Same values as list API: PURCHASE | SCRAP | ADJUSTMENT | OPENING_STOCK */
  transactionType?: string | null
}

/** Format date as dd-MM-yyyy for stock transaction export APIs */
export function formatStockTransactionExportDate(d: Date): string {
  const day = d.getDate().toString().padStart(2, '0')
  const month = (d.getMonth() + 1).toString().padStart(2, '0')
  const year = d.getFullYear()
  return `${day}-${month}-${year}`
}

export function getStockTransactionPdf(params: StockTransactionExportParams) {
  const searchParams = new URLSearchParams()
  if (params.vendorId != null && params.vendorId > 0) {
    searchParams.set('vendor_id', String(params.vendorId))
  }
  if (params.from) searchParams.set('from', params.from)
  if (params.to) searchParams.set('to', params.to)
  if (params.transactionType) {
    searchParams.set('transaction_type', params.transactionType)
  }
  const query = searchParams.toString()
  const url = query
    ? `/stock_invoice/pvt/stock_invoice_pdf?${query}`
    : '/stock_invoice/pvt/stock_invoice_pdf'
  return http
    .get<StockTransactionExportResponse>(url)
    .then((res: AxiosResponse<StockTransactionExportResponse>) => res.data)
}

export function getStockTransactionExcel(params: StockTransactionExportParams) {
  const searchParams = new URLSearchParams()
  if (params.vendorId != null && params.vendorId > 0) {
    searchParams.set('vendor_id', String(params.vendorId))
  }
  if (params.from) searchParams.set('from', params.from)
  if (params.to) searchParams.set('to', params.to)
  if (params.transactionType) {
    searchParams.set('transaction_type', params.transactionType)
  }
  const query = searchParams.toString()
  const url = query
    ? `/stock_invoice/pvt/stock_invoice_excel?${query}`
    : '/stock_invoice/pvt/stock_invoice_excel'
  return http
    .get<StockTransactionExportResponse>(url)
    .then((res: AxiosResponse<StockTransactionExportResponse>) => res.data)
}

export interface UpdateStockTransactionRequest {
  type: AddItemStockType
  quantity: number
  rate?: number
  forceUpdate?: boolean
  transactionDate?: string
  reference?: string
  vendorId?: number
  invoiceNo?: string
  invoiceValue?: number
}

export interface UpdateStockTransactionResponse {
  success: boolean
  message: string
  data?: StockTransactionListItem
}

export function updateStockTransaction(
  id: number,
  body: UpdateStockTransactionRequest
) {
  return http
    .put<UpdateStockTransactionResponse>(`/item/pvt/stock-transaction/${id}`, body)
    .then((response: AxiosResponse<UpdateStockTransactionResponse>) => response.data)
}

export interface DeleteStockTransactionResponse {
  success: boolean
  message: string
  data: null
}

export function deleteStockTransaction(id: number) {
  return http
    .delete<DeleteStockTransactionResponse>(`/item/pvt/stock-transaction/${id}`)
    .then((response: AxiosResponse<DeleteStockTransactionResponse>) => response.data)
}

export interface ItemHistoryRow {
  /** Date-time string e.g. "19-02-2026 14:30 +0530" */
  date: string
  party: string | null
  action: string
  inQuantity: number
  outQuantity: number
  lostQuantity: number
  issued: number
  available: number
  lost: number
  ref: string | null
  siteId?: number | null
  partyId?: number | null
  challanId?: number | null
  billId?: number | null
  entryId?: number
  entryType?: string
}

export interface ItemHistoryData {
  itemHistoryRows: ItemHistoryRow[]
  availableQuantity: number
  issuedQuantity: number
  lostQuantity: number
  purchaseQuantity: number
  scrapQuantity: number
  hiredQuantity: number
  warningMessage?: string
  /** When true, show history warning and per-row Available column */
  stockValidationEnabled?: boolean
}

export interface GetItemHistoryResponse {
  success: boolean
  message?: string
  data: ItemHistoryData
}

export function getItemHistory(itemId: number, fromDate?: string) {
  let url = `/item/pvt/history?itemId=${itemId}`
  if (fromDate) {
    url = `${url}&from_date=${fromDate}`
  }
  return http
    .get<GetItemHistoryResponse>(url)
    .then((response: AxiosResponse<GetItemHistoryResponse>) => response.data)
}

export interface ItemHistoryExportResponse {
  success: boolean
  message?: string
  data: string
}

function getItemHistoryExportUrl(
  basePath: '/item/pvt/history_pdf' | '/item/pvt/history_excel',
  itemId: number,
  fromDate?: string
) {
  const query = new URLSearchParams()
  query.set('itemId', String(itemId))
  if (fromDate) {
    query.set('from_date', fromDate)
  }
  return `${basePath}?${query.toString()}`
}

export function getItemHistoryPdf(itemId: number, fromDate?: string) {
  const url = getItemHistoryExportUrl('/item/pvt/history_pdf', itemId, fromDate)
  return http
    .get<ItemHistoryExportResponse>(url)
    .then((response: AxiosResponse<ItemHistoryExportResponse>) => response.data)
}

export function getItemHistoryExcel(itemId: number, fromDate?: string) {
  const url = getItemHistoryExportUrl('/item/pvt/history_excel', itemId, fromDate)
  return http
    .get<ItemHistoryExportResponse>(url)
    .then((response: AxiosResponse<ItemHistoryExportResponse>) => response.data)
}
